// Angular imports
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

// UAPP Core Imports
import { CoreLanguageService } from "../services/language.service";
import { ValueStorageService, StorageHive, LocalKeys } from "./valueStorage.service";
import Utils from "../shared/utils";
import { CoreLogService } from "./log.service";
import { SettingsService } from "./settings.service";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { InternationalisationService } from "../forked/angular-internationalisation";

@Injectable()
export class CommunicationData {

  // Hält globale BehaviorSubject bereit, damit Komponenten untereinander kommunizieren können.
  // Dies ist sinnvoll, wenn mehrere Komponenten von derselben Eigenschaft abhängig sind.
  // Dann wird diese Eigenschaft hier global gesetzt und alle abhängigen Komponenten werden durch einen
  // Subscriber auf den Observer informiert.
  // Wichtig: .unsubscribe() nach Benutzung des Subscribers nicht vergessen!

  /** Zeigt an, ob das Menu ein- oder ausgeblendet ist.
   * true - eingeblendet
   * false - ausgeblendet
   */
  private _showMenu = new BehaviorSubject<boolean>(false);

  /** Layoutauswahl
   * layout-colorful
   * layout-light
   * layout-dark
   */
  private _layoutClass = new BehaviorSubject<string>("layout-colorful");

  /** Sprachauswahl
   * de - deutsch
   * en - englisch
   */
  private _languageChoice = new BehaviorSubject<string>("");

  /** Zeigt an, ob der Pushdienst ein- oder ausgeschaltet ist.
  * true - eingeschaltet
  * false - ausgeschaltet
  */
  private _pushChoice = new BehaviorSubject<boolean>(false);

  /** Zeigt an, ob der Sound ein- oder ausgeschaltet ist.
  * true - eingeschaltet
  * false - ausgeschaltet
  */
  private _soundChoice = new BehaviorSubject<boolean>(false);

  constructor(private _coreLanguageService: CoreLanguageService,
    private _coreLogService: CoreLogService,
    private _settingsService: SettingsService,
    private _valueStorage: ValueStorageService,
    private _objObsInjectHelper: CircularInjectionHelper) {
    const me = this;
    // Gespeicherten Wert für die Auswahl des Layouts aus der Datenbank des Gerätes holen.
    me._valueStorage.get(StorageHive.Local, LocalKeys.layoutClass).then(_layoutClass => {
      if (Utils.IsNull(_layoutClass)) {
        // Wert mit Standardeinstellung in der Datenbank anlegen.
        me.setLayoutClass("layout-colorful");
      } else {
        me.setLayoutClassNoDatabase(_layoutClass);
      }
    });
    me._objObsInjectHelper.yourAppServer.Api.getLanguage().then((language) => {
      me.setLanguageChoiceNoDatabase(language);
    }).catch((e) => {
      me._coreLogService.error("CommunicationData.constructor", "yourAppServer.Api.getLanguage", e);
      me.setLanguageChoiceNoDatabase(InternationalisationService.locale);
    });

    // Gespeicherten Wert für die Auswahl des Pushdienstes aus der Datenbank des Gerätes holen.
    me._valueStorage.get(StorageHive.Local, LocalKeys.pushChoice).then(_pushChoice => {
      if (Utils.IsNull(_pushChoice)) {
        // Wert mit Standardeinstellung in der Datenbank anlegen.
        me.setPushChoice(true);
      } else {
        me.setPushChoiceNoDatabase(_pushChoice);
      }
    });
    // Gespeicherten Wert für die Auswahl der Soundwiedergabe aus der Datenbank des Gerätes holen.
    me._valueStorage.get(StorageHive.Local, LocalKeys.soundChoice).then(_soundChoice => {
      if (Utils.IsNull(_soundChoice)) {
        // Wert mit Standardeinstellung in der Datenbank anlegen.
        me.setSoundChoice(true);
      } else {
        me.setSoundChoiceNoDatabase(_soundChoice);
      }
    });
  }

  private OnInit() {
    // Wird hier nie aufgerufen, da wir keine Komponente sind.
  }

  public ShowMenu(): Observable<boolean> {
    return this._showMenu.asObservable();
  }
  public setShowMenu(_showMenu: boolean) {
    this._showMenu.next(_showMenu);
  }

  public LayoutClass(): Observable<string> {
    return this._layoutClass.asObservable();
  }
  public setLayoutClass(_layoutClass: string) {
    // vorher Änderung in der Datenbank speichern.
    this._valueStorage.set(StorageHive.Local, LocalKeys.layoutClass, _layoutClass);
    this._layoutClass.next(_layoutClass);
  }
  private setLayoutClassNoDatabase(_layoutClass: string) {
    this._layoutClass.next(_layoutClass);
  }

  public LanguageChoice(): Observable<string> {
    return this._languageChoice.asObservable();
  }
  public setLanguageChoice(languageChoice: string) {
    // vorher Änderung in der Datenbank speichern.
    this._valueStorage.set(StorageHive.Local, LocalKeys.languageChoice, languageChoice);
    this._coreLanguageService.setCurrentLanguage(languageChoice);
    this.updateDevice();
    this._languageChoice.next(languageChoice);
  }
  private setLanguageChoiceNoDatabase(languageChoice: string) {
    this._coreLanguageService.setCurrentLanguage(languageChoice);
    this._languageChoice.next(languageChoice);
  }

  public PushChoice(): Observable<boolean> {
    return this._pushChoice.asObservable();
  }
  public setPushChoice(_pushChoice: boolean) {
    // vorher Änderung in der Datenbank speichern.
    this._valueStorage.set(StorageHive.Local, LocalKeys.pushChoice, _pushChoice);
    this._pushChoice.next(_pushChoice);
  }
  private setPushChoiceNoDatabase(_pushChoice: boolean) {
    this._pushChoice.next(_pushChoice);
  }

  // dazugehöriger Observer
  public SoundChoice(): Observable<boolean> {
    return this._soundChoice.asObservable();
  }
  public setSoundChoice(_soundChoice: boolean) {
    // vorher Änderung in der Datenbank speichern.
    this._valueStorage.set(StorageHive.Local, LocalKeys.soundChoice, _soundChoice);
    this._soundChoice.next(_soundChoice);
  }
  private setSoundChoiceNoDatabase(_soundChoice: boolean) {
    this._soundChoice.next(_soundChoice);
  }

  private updateDevice() {
    const me = this;
    me._objObsInjectHelper.yourAppServer.Api.UpdateDevice().then(success => {
      me._coreLogService.debug("communicationdataService.updateDevice", "Updated Device after Language Select=" + success);
    }).catch(error => {
      me._coreLogService.error("communicationdataService.updateDevice", "yourAppServer.Api.UpdateDevice", error);
    });
  }
}
